import React from "react";
import XopsTimeline from "./XopsTimeline";
import { BackgroundLayer, Group, IGanttAction, IGanttActionParamForRun, IGanttOnContextMenuShowParam, Item, RowLayer, Timeline } from '@crispico/react-timeline-10000';
import { GanttUtils } from "./GanttUtils";
import { MissionItemRenderer } from "./typeRenderers/MissionItemRenderer";
import { TaskItemRenderer } from "./typeRenderers/TaskItemRenderer";
import { MissionEventInfoRenderer } from "./typeRenderers/MissionEventInfoRenderer";
import { Reducers, ReduxReusableComponents, RRCProps, State } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import { ModalExt, ModalExtOpen } from "@crispico/foundation-react/components/ModalExt/ModalExt";
import { Modal } from "semantic-ui-react";
import { GanttInfoRenderer } from "./GanttInfoRenderer";
import { ContextMenuActionsProvider } from "components/ContextMenuRegistry";
import { EntityTablePage } from "@crispico/foundation-react";
import { ProposedTimeSlotItemRenderer } from "./typeRenderers/ProposedTimeSlotItemRenderer";
import { DragAndDropControllerNew } from "./DragAndDropControllerNew";
import { AssignTasksToResourceAction } from "pages/Task/actions/AssignTasksToResourceAction";
import { Scriptable, ScriptableContainer } from "@famiprog-foundation/scriptable-ui";
import { Draggable, Droppable, Identifier } from "./DragAndDrop";

export const DEFAULT_ITEM_HEIGHT = 40;
export const COMPACT_ITEM_HEIGHT = 25;
export const TIME_HOUR_OFFSET = 2;
export const MODAL_OFFSET = 10;

// @ts-ignore
export type XopsGanttPropsRRC = RRCProps<XopsGanttState, XopsGanttReducers> & XopsGanttProps;
// copied from Timeline so I can use the types

export type XopsGanttProps = {
    startDate: number | object,
    endDate: number | object,
    table: JSX.Element,
    tablePage: EntityTablePage,
    rowLayers?: RowLayer[],
    groups: Group[],
    items: (Item & { entity: any })[];

    compactMode: boolean,
    id: string // needed to propagate the RRC id
    activatedDnD?: boolean;
};

class XopsGanttState extends State {
    modalOpen: ModalExtOpen = false;
    entityUid: string | undefined;
    selectedItems: (number | string)[] = [];
}

class XopsGanttReducers<S extends XopsGanttState = XopsGanttState> extends Reducers<S> {

    updateModalEntityUid(entityUid?: string, value?: ModalExtOpen) {
        this.s.entityUid = entityUid;
        this.s.modalOpen = value ?? false;
    }
}

export class XopsGantt extends React.Component<XopsGanttPropsRRC> {

    protected timelineRef = React.createRef<XopsTimeline>();
    protected contextMenuActionsProviderRef = React.createRef<ContextMenuActionsProvider>();
    protected assignTaskActionRef = React.createRef<AssignTasksToResourceAction>();

    constructor(props: XopsGanttPropsRRC) {
        super(props);
        this.itemRenderer = this.itemRenderer.bind(this);
        this.onContextMenuShow = this.onContextMenuShow.bind(this);
        this.onItemHover = this.onItemHover.bind(this);
        this.onItemLeave = this.onItemLeave.bind(this);
        this.onItemClick = this.onItemClick.bind(this);
        this.onDrop = this.onDrop.bind(this);
    }

    public onDrop(source: any, sourceEntityType: Identifier, destination: any, destinationEntityType: Identifier) {
        let assignTasksToResourcePageProps = DragAndDropControllerNew.INSTANCE.getAssignTasksToResourcePageProps(source, sourceEntityType, destination, destinationEntityType);
        if (!assignTasksToResourcePageProps) {
            return;
        }
        this.assignTaskActionRef.current!.setState({ assignTasksToResourcePageProps });
    }

    // Added `disableDnD` because this method is also used in the `Quick info` modal,
    // where we need to remove the wrapper classes related to drag-and-drop .
    protected itemRenderer(itemProps: any, disableDnD?: boolean) {
        const { key } = itemProps.item;
        const { entityName } = GanttUtils.fromEntityUid(key);
        let renderClass;
        switch (entityName) {
            case "Mission2": {
                renderClass = MissionItemRenderer;
                break;
            }
            case "Task": {
                renderClass = TaskItemRenderer;
                break;
            }
            case "MissionEvent2": {
                renderClass = MissionEventInfoRenderer;
                break;
            }
            case "ProposedTimeSlot": {
                return <ProposedTimeSlotItemRenderer {...itemProps} />;
            }
        }
        if (!this.props.activatedDnD || disableDnD == true) {
            return React.createElement(renderClass as any, itemProps);
        }
        let selectedEntities = undefined;
        if (this.props.s.selectedItems.length) {
            selectedEntities = this.props.items.filter(item => this.props.s.selectedItems.includes(item.key)).map((item: any) => item.entity)
        }
        return renderClass ? <Droppable className="Droppable" key={key} item={itemProps.entity} type={entityName} accept={DragAndDropControllerNew.INSTANCE.getAcceptedType(entityName)} canDrop={DragAndDropControllerNew.INSTANCE.canDrop} drop={this.onDrop} >
            <Draggable className="Draggable" key={key} item={itemProps.entity} type={entityName} selectedItems={selectedEntities} canDrag={DragAndDropControllerNew.INSTANCE.canDrag} hideDragPreview={true} onDragStart={(e) => {
                this.props.r.updateModalEntityUid();
                if (!this.props.s.selectedItems.includes(key)) {
                    this.props.r.setInReduxState({ selectedItems: e.ctrlKey ? [...this.props.s.selectedItems, key] : [key] });
                }
            }}>
                {React.createElement(renderClass as any, itemProps)}
            </Draggable>
        </Droppable > : <></>;
    }

    protected onContextMenuShow(contextMenuShowParam: IGanttOnContextMenuShowParam): IGanttAction[] {
        const actions = this.contextMenuActionsProviderRef.current?.getActions() ?? [];
        if (contextMenuShowParam.actionParam.selection.length === 0) {
            // show contextMenu actions on gantt
            return actions;
        }
        if (contextMenuShowParam.actionParam.selection.length !== 1) {
            // show contextMenu only for one selected item
            return [];
        }

        return [{
            icon: "info",
            label: _msg("XopsGantt.contextMenu.quickInfo.label"),
            run: (_: IGanttActionParamForRun) => this.props.r.updateModalEntityUid(contextMenuShowParam.actionParam.selection[0] as string, true)
        }, ...actions];
    }

    protected onItemHover(itemKey: any, offsetX: number, offsetY: number) {
        if (this.props.s.entityUid !== itemKey) {
            this.props.r.updateModalEntityUid(itemKey, [offsetX + MODAL_OFFSET, offsetY + MODAL_OFFSET]);
        }
    }

    protected onItemLeave(_: React.MouseEvent, __: any) {
        this.props.r.updateModalEntityUid();
    }

    protected onItemClick(e: MouseEvent, itemKey: any) {
        let selectedItems: (string | number)[] = [];
        if (e.ctrlKey) {
            selectedItems.push(...this.props.s.selectedItems);
        }
        selectedItems.push(itemKey);
        this.props.r.setInReduxState({ selectedItems });
    }

    protected getItem(key: any) {
        return this.props.items.find(item => item.key === key);
    }

    protected getEntityUid(key: string | number): string {
        // string is used for items and number for rows
        if (typeof key === 'number') {
            const entity = this.props.tablePage.getEntityAt(key);
            if (!entity) {
                return "";
            }
            return GanttUtils.toEntityUid(this.props.tablePage.props.entityDescriptor.name, entity.id);
        }
        return key;
    }

    protected getEntityAt(key: string | number): { id: any;[key: string]: any; } | undefined {
        // string is used for items and number for rows
        if (typeof key === 'string') {
            return this.getItem(key)?.entity;
        }
        return this.props.tablePage.getEntityAt(key);
    }

    render() {
        const { props } = this;
        const asPopup = Array.isArray(this.props.s.modalOpen);
        const itemHeight = this.props.compactMode ? COMPACT_ITEM_HEIGHT : DEFAULT_ITEM_HEIGHT;
        const rowTopBottomPadding = this.props.compactMode ? 2.5 : 5; // DEFAULT_ITEM_HEIGHT + 5 + 5 = 50 the table height
        return <ScriptableContainer id={this.props.id + "XopsGantt"}>
            <div className={`flex-container-row flex-grow XopsGantt${this.props.compactMode ? ' XopsGantt_compactMode' : ''}`} >
                <XopsTimeline ref={this.timelineRef} splitPaneSizeInitial="50%" droppableProps={{}} forceRedrawFunc={() => true}
                    itemRenderer={this.itemRenderer} onItemHover={(e: React.MouseEvent, itemKey: any) => this.onItemHover(itemKey, e.clientX, e.clientY)} onItemLeave={this.onItemLeave}
                    onItemContextClick={() => this.props.r.updateModalEntityUid()} onContextMenuShow={this.onContextMenuShow}
                    itemHeight={itemHeight}
                    rowTopBottomPadding={rowTopBottomPadding}
                    componentId={this.props.id + "_XopsTimeline"} showCursorTime={false}
                    backgroundLayer={<BackgroundLayer nowMarker={true} />}
                    // @ts-ignore
                    timelineMode={Timeline.TIMELINE_MODES.SELECT}
                    selectedItems={this.props.s.selectedItems}
                    onItemClick={this.onItemClick}
                    onRowClick={() => this.props.r.setInReduxState({ selectedItems: [] })}
                    onRowContextClick={() => this.props.r.setInReduxState({ selectedItems: [] })}
                    {...this.props} />
                <ModalExt closeOnPortalMouseLeave={asPopup ? true : false} transparentDimmer={asPopup} closeOnEscape closeIcon={asPopup ? false : true} style={{ maxWidth: '750px' }} open={this.props.s.modalOpen} onClose={() => this.props.r.updateModalEntityUid()}>
                    {!asPopup && <Modal.Header className="small-padding">
                        {props.s.entityUid &&
                            <div className="flex-grow flex-justify-content-center">
                                <div style={{ minWidth: '100px', maxWidth: '350px' }}>{this.itemRenderer({ ...this.getItem(props.s.entityUid), item: { key: props.s.entityUid }, className: "rct9k-items-inner" }, true)}</div>
                            </div>
                        }
                    </Modal.Header>}
                    <Modal.Content className="small-padding flex-grow" style={{ maxHeight: asPopup ? undefined : '300px' }}>
                        {this.props.s.entityUid && <GanttInfoRenderer entityName={GanttUtils.fromEntityUid(this.props.s.entityUid).entityName} entity={this.getItem(props.s.entityUid)?.entity} />}
                    </Modal.Content>
                </ModalExt>
            </div>
            <ContextMenuActionsProvider ref={this.contextMenuActionsProviderRef} getEntityUid={(key: string | number) => this.getEntityUid(key)} getEntityAt={(key: string | number) => this.getEntityAt(key)}
                tablePage={this.props.tablePage} />
            {this.props.activatedDnD ? <AssignTasksToResourceAction ref={this.assignTaskActionRef} getEntityUid={() => ""} getEntityAt={() => ({ id: null })} tablePage={this.props.tablePage} /> : null}
        </ScriptableContainer>
    }
}

export const XopsGanttRRC = ReduxReusableComponents.connectRRC(XopsGanttState, XopsGanttReducers, XopsGantt);
